import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Calculator, Keyboard, X, Sigma } from "lucide-react";
import { renderMarkdown } from "@/lib/renderMarkdown";

/* Math Tools popover — combines a LaTeX symbol keyboard and a sandboxed
 * scientific calculator. Used in ChatPage and PastPapersPage so students
 * can quickly type math expressions or compute intermediate values.
 *
 * Props:
 *   insertText(s):  callback that inserts `s` at the cursor of the parent's
 *                   active textarea (parent owns the textarea ref).
 */

const SYMBOL_GROUPS = [
  {
    title: "Common",
    items: [
      { label: "²", insert: "^{2}" },
      { label: "³", insert: "^{3}" },
      { label: "xⁿ", insert: "^{}" },
      { label: "√", insert: "\\sqrt{}" },
      { label: "ⁿ√", insert: "\\sqrt[n]{}" },
      { label: "a/b", insert: "\\frac{}{}" },
      { label: "π", insert: "\\pi" },
      { label: "∞", insert: "\\infty" },
      { label: "°", insert: "^\\circ" },
      { label: "±", insert: "\\pm" },
      { label: "≤", insert: "\\leq" },
      { label: "≥", insert: "\\geq" },
      { label: "≠", insert: "\\neq" },
      { label: "≈", insert: "\\approx" },
      { label: "·", insert: "\\cdot" },
      { label: "÷", insert: "\\div" },
    ],
  },
  {
    title: "Greek",
    items: [
      { label: "α", insert: "\\alpha" },
      { label: "β", insert: "\\beta" },
      { label: "γ", insert: "\\gamma" },
      { label: "θ", insert: "\\theta" },
      { label: "λ", insert: "\\lambda" },
      { label: "μ", insert: "\\mu" },
      { label: "σ", insert: "\\sigma" },
      { label: "φ", insert: "\\phi" },
      { label: "ω", insert: "\\omega" },
      { label: "Δ", insert: "\\Delta" },
      { label: "Σ", insert: "\\Sigma" },
      { label: "Π", insert: "\\Pi" },
    ],
  },
  {
    title: "Functions",
    items: [
      { label: "sin", insert: "\\sin " },
      { label: "cos", insert: "\\cos " },
      { label: "tan", insert: "\\tan " },
      { label: "log", insert: "\\log_{}" },
      { label: "ln", insert: "\\ln " },
      { label: "lim", insert: "\\lim_{x \\to }" },
      { label: "∫", insert: "\\int_{}^{} " },
      { label: "∑", insert: "\\sum_{i=1}^{n} " },
      { label: "d/dx", insert: "\\frac{d}{dx}" },
      { label: "→", insert: "\\to" },
      { label: "f(x)", insert: "f(x)" },
      { label: "₁/₂", insert: "\\frac{1}{2}" },
    ],
  },
];

// Whitelist of math functions exposed to the calculator's Function() eval
const CALC_FNS = {
  sin: (x) => Math.sin(toRad(x)),
  cos: (x) => Math.cos(toRad(x)),
  tan: (x) => Math.tan(toRad(x)),
  asin: (x) => fromRad(Math.asin(x)),
  acos: (x) => fromRad(Math.acos(x)),
  atan: (x) => fromRad(Math.atan(x)),
  log: Math.log10,
  ln: Math.log,
  sqrt: Math.sqrt,
  abs: Math.abs,
  exp: Math.exp,
  pow: Math.pow,
  fact: (n) => {
    n = Math.floor(n);
    if (n < 0) return NaN;
    let r = 1;
    for (let i = 2; i <= n; i++) r *= i;
    return r;
  },
  pi: Math.PI,
  e: Math.E,
};

let _degMode = true;
const toRad = (x) => (_degMode ? (x * Math.PI) / 180 : x);
const fromRad = (x) => (_degMode ? (x * 180) / Math.PI : x);

const SAFE_TOKEN_RE =
  /^[\s\d+\-*/().,^!]+$|^(sin|cos|tan|asin|acos|atan|log|ln|sqrt|abs|exp|pow|fact|pi|e)$/i;

const safeEval = (expr) => {
  // Replace user-friendly symbols
  let js = expr
    .replace(/\s+/g, "")
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/π/g, "pi")
    .replace(/√/g, "sqrt")
    .replace(/\^/g, "**");
  // Factorial: 5! → fact(5), (expr)! → fact((expr))
  js = js.replace(/(\d+(?:\.\d+)?|\))!/g, (_, x) => `fact(${x})`);
  // Tokenise + validate
  const tokens = js.match(/[A-Za-z_]+|\d+(\.\d+)?|[+\-*/()^,]|\*\*/g) || [];
  for (const t of tokens) {
    if (/^[A-Za-z_]+$/.test(t) && !(t in CALC_FNS)) {
      throw new Error(`Unknown identifier '${t}'`);
    }
  }
  // eslint-disable-next-line no-new-func
  const result = new Function(
    ...Object.keys(CALC_FNS),
    `"use strict"; return (${js});`,
  )(...Object.values(CALC_FNS));
  if (typeof result !== "number" || Number.isNaN(result)) {
    throw new Error("Result is not a number");
  }
  return result;
};

export function MathTools({ insertText }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("symbols");
  const [calcExpr, setCalcExpr] = useState("");
  const [calcResult, setCalcResult] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const [deg, setDeg] = useState(true);
  const popoverRef = useRef(null);

  useEffect(() => {
    _degMode = deg;
  }, [deg]);

  const onSymbol = (s) => {
    if (insertText) insertText(s);
  };

  const runCalc = () => {
    if (!calcExpr.trim()) return;
    try {
      const r = safeEval(calcExpr);
      const rounded = Number.isInteger(r) ? r : Number(r.toFixed(8));
      setCalcResult(String(rounded));
      setCalcError(null);
    } catch (e) {
      setCalcError(e.message || "Invalid expression");
      setCalcResult(null);
    }
  };

  const appendCalc = (s) => {
    setCalcExpr((prev) => prev + s);
    setCalcError(null);
  };

  const insertResult = () => {
    if (calcResult != null && insertText) {
      insertText(calcResult);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="math-tools-toggle"
        title="Math symbols & calculator"
        className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-xs sm:text-sm px-3 py-2"
      >
        <Sigma className="w-4 h-4" />
        <span className="hidden sm:inline">Math</span>
      </button>

      {open && createPortal(
        <>
          {/* Backdrop — tap outside to close (mobile + desktop) */}
          <button
            type="button"
            aria-label="Close math tools"
            onClick={() => setOpen(false)}
            data-testid="math-tools-backdrop"
            className="fixed inset-0 bg-black/40 sm:bg-transparent z-[60]"
          />
          <div
            data-testid="math-tools-popover"
            className="fixed left-2 right-2 bottom-2 max-h-[85vh] overflow-y-auto rounded-3xl
                       sm:left-auto sm:right-6 sm:bottom-24 sm:w-[420px] sm:max-h-[70vh] sm:rounded-2xl
                       p-4 shadow-2xl z-[61] bg-white border border-border"
          >
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setTab("symbols")}
                data-testid="math-tab-symbols"
                className={`pill-btn text-xs px-3 py-1.5 ${
                  tab === "symbols"
                    ? "bg-[hsl(var(--brimi-blue))] text-white"
                    : "bg-foreground/5 text-foreground"
                }`}
              >
                <Keyboard className="w-3.5 h-3.5" /> Symbols
              </button>
              <button
                type="button"
                onClick={() => setTab("calc")}
                data-testid="math-tab-calc"
                className={`pill-btn text-xs px-3 py-1.5 ${
                  tab === "calc"
                    ? "bg-[hsl(var(--brimi-blue))] text-white"
                    : "bg-foreground/5 text-foreground"
                }`}
              >
                <Calculator className="w-3.5 h-3.5" /> Calculator
              </button>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="math-tools-close"
              aria-label="Close"
              className="p-1 rounded-full hover:bg-foreground/5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {tab === "symbols" && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {SYMBOL_GROUPS.map((g) => (
                <div key={g.title}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                    {g.title}
                  </div>
                  <div className="grid grid-cols-6 gap-1.5">
                    {g.items.map((it) => (
                      <button
                        key={it.label}
                        type="button"
                        onClick={() => onSymbol(it.insert)}
                        title={`Insert ${it.insert}`}
                        data-testid={`math-sym-${it.label}`}
                        className="aspect-square rounded-lg bg-foreground/5 hover:bg-[hsl(var(--brimi-blue))]/15 hover:text-[hsl(var(--brimi-blue))] text-base font-bold transition-colors flex items-center justify-center"
                      >
                        {it.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                Inserts LaTeX (e.g. <code>\sqrt{`{}`}</code>) — your $...$ math
                will render automatically in chat replies.
              </div>
            </div>
          )}

          {tab === "calc" && (
            <div className="space-y-3">
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeg((d) => !d)}
                  data-testid="math-calc-mode"
                  className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs px-3 py-1"
                >
                  {deg ? "DEG" : "RAD"}
                </button>
              </div>
              <input
                value={calcExpr}
                onChange={(e) => setCalcExpr(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runCalc();
                  }
                }}
                placeholder="e.g. 5! + sin(30) * sqrt(2)"
                data-testid="math-calc-input"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-base font-mono outline-none focus:border-[hsl(var(--brimi-blue))]"
              />
              <div className="grid grid-cols-5 gap-1.5 text-sm">
                {[
                  "7",
                  "8",
                  "9",
                  "(",
                  ")",
                  "4",
                  "5",
                  "6",
                  "×",
                  "÷",
                  "1",
                  "2",
                  "3",
                  "+",
                  "-",
                  "0",
                  ".",
                  "^",
                  "!",
                  "π",
                ].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => appendCalc(k)}
                    className="aspect-square rounded-lg bg-foreground/5 hover:bg-foreground/10 font-bold"
                  >
                    {k}
                  </button>
                ))}
                {[
                  "sin(",
                  "cos(",
                  "tan(",
                  "log(",
                  "ln(",
                  "sqrt(",
                  "exp(",
                  "abs(",
                  "asin(",
                  "acos(",
                ].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => appendCalc(k)}
                    className="rounded-lg bg-foreground/5 hover:bg-foreground/10 text-xs py-2 font-bold col-span-1"
                  >
                    {k.replace("(", "")}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCalcExpr("");
                    setCalcResult(null);
                    setCalcError(null);
                  }}
                  className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs px-3 py-1.5"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={runCalc}
                  data-testid="math-calc-equals"
                  className="pill-btn bg-[hsl(var(--brimi-coral))] hover:bg-[hsl(var(--brimi-coral))]/90 text-white text-sm px-4 py-1.5 ml-auto"
                >
                  =
                </button>
              </div>
              {calcResult != null && (
                <div
                  data-testid="math-calc-result"
                  className="p-3 rounded-xl bg-[hsl(var(--brimi-mint))]/20 border border-[hsl(var(--brimi-mint))]/40 flex items-center justify-between"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      Result
                    </div>
                    <div className="text-xl font-black font-mono">
                      {calcResult}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={insertResult}
                    data-testid="math-calc-insert"
                    className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-xs px-3 py-1.5"
                  >
                    Insert
                  </button>
                </div>
              )}
              {calcError && (
                <div
                  data-testid="math-calc-error"
                  className="p-2 rounded-xl bg-destructive/10 text-destructive text-xs font-mono"
                >
                  {calcError}
                </div>
              )}
            </div>
          )}
        </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/* Small helper: inserts `s` into a textarea at the current cursor position,
 * placing the caret inside the first empty braces if any (e.g. \sqrt{|}).
 * Returns a function to bind to MathTools' insertText prop. */
export const makeInsertHandler = (textareaRef, setValue) => (s) => {
  const el = textareaRef.current;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);
  // Find the first `{}` placeholder in the inserted string for caret positioning.
  const firstBrace = s.indexOf("{}");
  const newValue = before + s + after;
  setValue(newValue);
  // Reposition caret after React updates
  requestAnimationFrame(() => {
    el.focus();
    const caret =
      firstBrace >= 0
        ? before.length + firstBrace + 1
        : before.length + s.length;
    el.setSelectionRange(caret, caret);
  });
};
